import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { CustodyCase, HistoryEntry } from '../types/case';
import type { AppConfig, SystemLogEntry } from '../types/config';
import { seedCases } from '../data/cases';
import { defaultConfig } from '../data/config';
import { nowIso } from '../utils/bn';
type NewCaseInput = Omit<CustodyCase, 'pcr' | 'receiptNo' | 'history' | 'createdAt'>;
interface StoreValue {
  cases: CustodyCase[];
  config: AppConfig;
  systemLog: SystemLogEntry[];
  getCase: (pcr: string) => CustodyCase | undefined;
  nextPcr: () => string;
  createCase: (input: NewCaseInput, entry: Omit<HistoryEntry, 'at' | 'key'>) => CustodyCase;
  updateCase: (pcr: string, fn: (c: CustodyCase) => CustodyCase, entry?: Omit<HistoryEntry, 'at'>) => void;
  updateConfig: (fn: (c: AppConfig) => AppConfig, log: Omit<SystemLogEntry, 'at'>) => void;
  resetDemo: () => void;
}
interface PersistShape {
  cases: CustodyCase[];
  config: AppConfig;
  systemLog: SystemLogEntry[];
}
const KEY = 'dmp-vdrms-demo-v1';
const StoreContext = createContext<StoreValue | null>(null);
function initialState(): PersistShape {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as PersistShape;
  } catch {
    /* ignore storage errors in sandboxed preview */
  }
  return {
    cases: seedCases,
    config: defaultConfig,
    systemLog: []
  };
}
export function StoreProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<PersistShape>(initialState);
  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
  }, [state]);
  const getCase = useCallback((pcr: string) => state.cases.find(c => c.pcr === pcr), [state.cases]);
  const nextPcr = useCallback(() => {
    const max = state.cases.reduce((m, c) => Math.max(m, Number(c.pcr.slice(-5))), 0);
    return `PCR-2026-${String(max + 1).padStart(5, '0')}`;
  }, [state.cases]);
  const createCase = useCallback<StoreValue['createCase']>((input, entry) => {
    const at = nowIso();
    const max = state.cases.reduce((m, c) => Math.max(m, Number(c.pcr.slice(-5))), 0);
    const rmax = state.cases.reduce((m, c) => Math.max(m, Number(c.receiptNo.slice(-5))), 0);
    const pcr = `PCR-2026-${String(max + 1).padStart(5, '0')}`;
    const created: CustodyCase = {
      ...input,
      pcr,
      receiptNo: `RCP-2026-${String(rmax + 1).padStart(5, '0')}`,
      createdAt: at,
      history: [{
        ...entry,
        key: 'created',
        at
      }]
    };
    setState(s => ({
      ...s,
      cases: [created, ...s.cases]
    }));
    return created;
  }, [state.cases]);
  const updateCase = useCallback<StoreValue['updateCase']>((pcr, fn, entry) => {
    setState(s => ({
      ...s,
      cases: s.cases.map(c => {
        if (c.pcr !== pcr) return c;
        const next = fn(c);
        return entry ? {
          ...next,
          history: [...next.history, {
            ...entry,
            at: nowIso()
          }]
        } : next;
      })
    }));
  }, []);
  const updateConfig = useCallback<StoreValue['updateConfig']>((fn, log) => {
    setState(s => ({
      ...s,
      config: fn(s.config),
      systemLog: [{
        ...log,
        at: nowIso()
      }, ...s.systemLog]
    }));
  }, []);
  const resetDemo = useCallback(() => {
    setState({
      cases: seedCases,
      config: defaultConfig,
      systemLog: []
    });
  }, []);
  const value = useMemo<StoreValue>(() => ({
    ...state,
    getCase,
    nextPcr,
    createCase,
    updateCase,
    updateConfig,
    resetDemo
  }), [state, getCase, nextPcr, createCase, updateCase, updateConfig, resetDemo]);
  return <StoreContext.Provider value={value} data-id="element-405" data-mp="1xsq1nv2ldmhd">{children}</StoreContext.Provider>;
}
export function useStore(): StoreValue {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
}